class HeaderCountdown extends HTMLElement {
  constructor() {
    super();
    this.endDate = 0;
    this.durationMs = 0;
    this.isCountdownEnabled = false;
    this.countdownInterval = null;
    this.scrollHandler = null;
    this.headerElement = null;
    this.fixedElement = null;
    this.headerContainer = null;
    this.fixedContainer = null;
    this.headerText = null;
    this.fixedText = null;
    this.textTemplate = '';
    this.expiredText = '';
    this.storageKey = 'header-countdown';
    this.resetWindowMs = 24 * 60 * 60 * 1000;
    this.updateCountdown = this.updateCountdown.bind(this);
  }

  connectedCallback() {
    this.cacheElements();
    this.updateHeaderContentVisibility();
    this.parseAttributes();
    this.resolveEndDate();
    this.initializeCountdown();
    this.setupScrollListener();
  }

  disconnectedCallback() {
    this.cleanup();
  }

  cacheElements() {
    this.headerElement = this.querySelector('.header-countdown--header');
    this.fixedElement = this.querySelector('.header-countdown--fixed');
    this.headerContainer = this.headerElement?.querySelector('.header-countdown__container');
    this.fixedContainer = this.fixedElement?.querySelector('.header-countdown__container');
    this.headerText = this.headerElement?.querySelector('[js-header-countdown="text"]') || null;
    this.headerDiscount = this.headerElement?.querySelector('[js-header-countdown="discount"]') || null;
    this.fixedText = this.fixedElement?.querySelector('[js-header-countdown="text"]') || null;
    this.textTemplate = String(this.parseJSONAttribute('data-countdown-text') || '');
    this.expiredText = String(this.parseJSONAttribute('data-countdown-expired-text') || '');
    this.countdownBefore = String(this.parseJSONAttribute('data-countdown-before') || '');
    this.countdownAfter = String(this.parseJSONAttribute('data-countdown-after') || '');
  }

  parseJSONAttribute(attributeName) {
    const rawValue = this.getAttribute(attributeName);

    if (!rawValue) {
      return '';
    }

    try {
      return JSON.parse(rawValue);
    } catch (error) {
      return '';
    }
  }

  getCookieValue(name) {
    if (typeof document === 'undefined' || !name) {
      return '';
    }

    const escapedName = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const cookieMatch = document.cookie.match(new RegExp(`(?:^|; )${escapedName}=([^;]*)`));
    return cookieMatch ? decodeURIComponent(cookieMatch[1]) : '';
  }

  updateHeaderContentVisibility() {
    if (!this.headerText || !this.headerDiscount) {
      return;
    }

    const discountCode = this.getCookieValue('discount_code');
    const discountCodeHandle = String(discountCode || '').trim().toLowerCase();
    const discountLookupHandle = discountCodeHandle.startsWith('rc-')
      ? 'recharge-referal'
      : discountCodeHandle;

    if (!discountCodeHandle) {
      this.setHeaderContentVisibility(false);
      return;
    }

    this.lookupDiscountCodeMetaobject(discountLookupHandle)
      .then((metaobject) => {
        const discountMessage = this.getDiscountMessageFromMetaobject(metaobject);
        const shouldShowDiscount = Boolean(discountMessage);

        if (shouldShowDiscount) {
          const safeMessage = discountMessage
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/\n/g, '<br>');
          this.headerDiscount.innerHTML = safeMessage;
        }

        this.setHeaderContentVisibility(shouldShowDiscount);
      })
      .catch(() => {
        this.setHeaderContentVisibility(false);
      });
  }

  setHeaderContentVisibility(showDiscount) {
    if (!this.headerText || !this.headerDiscount) {
      return;
    }

    this.headerDiscount.style.display = showDiscount ? 'block' : 'none';
    this.headerText.style.display = showDiscount ? 'none' : 'block';
  }

  isValidDiscountMetaobject(metaobject) {
    return Boolean(this.getDiscountMessageFromMetaobject(metaobject));
  }

  getDiscountMessageFromMetaobject(metaobject) {
    if (!metaobject || typeof metaobject !== 'object') {
      return '';
    }

    const fields = Array.isArray(metaobject.fields) ? metaobject.fields : [];
    const messageField = fields.find((field) => {
      const key = String(field?.key || '').trim().toLowerCase();
      return key === 'message';
    });

    return String(messageField?.value || '').trim();
  }

  getStorefrontApiVersion() {
    const configuredVersion = String(this.dataset.storefrontApiVersion || '').trim();
    return configuredVersion || '2025-01';
  }

  getStorefrontApiUrl() {
    const rootPath = String(window?.Shopify?.routes?.root || '/').trim() || '/';
    const normalizedRootPath = rootPath.endsWith('/') ? rootPath : `${rootPath}/`;
    return `${normalizedRootPath}api/${this.getStorefrontApiVersion()}/graphql.json`;
  }

  getStorefrontAccessToken() {
    const tokenCandidates = [
      this.dataset.storefrontAccessToken,
      window?.Shopify?.storefrontAccessToken,
      window?.theme?.storefrontAccessToken,
      window?.shopifyStorefrontAccessToken
    ];

    for (const tokenCandidate of tokenCandidates) {
      const normalizedToken = String(tokenCandidate || '').trim();

      if (normalizedToken) {
        return normalizedToken;
      }
    }

    return '';
  }

  getDiscountCodeMetaobjectQuery() {
    return `
      query GetDiscountCodeMessage($handle: MetaobjectHandleInput!) {
        metaobject(handle: $handle) {
          id
          handle
          fields {
            key
            value
          }
        }
      }
    `.trim();
  }

  async lookupDiscountCodeMetaobject(handle) {
    const normalizedHandle = String(handle || '').trim();
    const storefrontAccessToken = this.getStorefrontAccessToken();

    if (!normalizedHandle || !storefrontAccessToken) {
      return null;
    }

    const response = await fetch(this.getStorefrontApiUrl(), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': storefrontAccessToken
      },
      body: JSON.stringify({
        query: this.getDiscountCodeMetaobjectQuery(),
        variables: {
          handle: {
            handle: normalizedHandle,
            type: 'discount_code_message'
          }
        }
      })
    });

    if (!response.ok) {
      throw new Error(`Storefront API request failed with status ${response.status}`);
    }

    const payload = await response.json();
    const errors = Array.isArray(payload?.errors) ? payload.errors : [];

    if (errors.length) {
      const errorMessage = errors
        .map((error) => String(error?.message || '').trim())
        .filter(Boolean)
        .join(', ');
      throw new Error(errorMessage || 'Storefront API returned errors');
    }

    return payload?.data?.metaobject || null;
  }

  parseAttributes() {
    this.isCountdownEnabled = this.textTemplate.includes('##countdown##');
    const durationMinutes = Number(this.getAttribute('data-duration-minutes'));

    if (this.isCountdownEnabled && Number.isFinite(durationMinutes) && durationMinutes > 0) {
      this.durationMs = Math.round(durationMinutes * 60 * 1000);
    } else {
      this.durationMs = 0;
    }

    const storageKey = String(
      this.getAttribute('data-storage-key') || this.getAttribute('data-cookie-key') || ''
    ).trim();

    if (storageKey) {
      this.storageKey = storageKey;
    }
  }

  resolveEndDate() {
    if (this.durationMs <= 0) {
      this.endDate = 0;
      return;
    }

    const now = Date.now();
    const storedVisitTimestamp = Number(this.getStorageValue(this.storageKey));
    let visitTimestamp = storedVisitTimestamp;

    if (
      !Number.isFinite(visitTimestamp) ||
      visitTimestamp <= 0 ||
      visitTimestamp > now ||
      now - visitTimestamp >= this.resetWindowMs
    ) {
      visitTimestamp = now;
      this.setStorageValue(this.storageKey, String(visitTimestamp));
    }

    this.endDate = visitTimestamp + this.durationMs;
  }

  initializeCountdown() {
    if (!this.isCountdownEnabled || !this.endDate) {
      return;
    }

    this.updateCountdown();
    this.countdownInterval = window.setInterval(this.updateCountdown, 1000);
  }

  updateCountdown() {
    if (!this.isCountdownEnabled || !this.endDate) {
      return;
    }

    const now = Date.now();
    const timeDiff = Math.floor((this.endDate - now) / 1000);

    if (timeDiff <= 0) {
      this.showExpired();
      if (this.countdownInterval) {
        clearInterval(this.countdownInterval);
        this.countdownInterval = null;
      }
      return;
    }

    const hours = Math.floor(timeDiff / 3600);
    const minutes = Math.floor((timeDiff % 3600) / 60);
    const seconds = timeDiff % 60;

    const formattedHours = String(hours).padStart(2, '0');
    const formattedMinutes = String(minutes).padStart(2, '0');
    const formattedSeconds = String(seconds).padStart(2, '0');

    let countdownText = formattedSeconds;

    if (hours > 0) {
      countdownText = `${formattedHours}:${formattedMinutes}:${formattedSeconds}`;
    } else if (minutes > 0) {
      countdownText = `${formattedMinutes}:${formattedSeconds}`;
    }

    const countdownMarkup = `<span class="countdown-value">${countdownText}</span>`;
    const displayText = this.textTemplate.replace(/##countdown##/g, countdownMarkup);

    if (this.headerText) {
      this.headerText.innerHTML = displayText;
    } else if (this.headerContainer) {
      this.headerContainer.innerHTML = displayText;
    }

    if (this.fixedText) {
      this.fixedText.innerHTML = displayText;
    } else if (this.fixedContainer) {
      this.fixedContainer.innerHTML = displayText;
    }
  }

  showExpired() {
    const expiredText = this.expiredText || '';

    if (this.headerText) {
      this.headerText.innerHTML = expiredText;
    } else if (this.headerContainer) {
      this.headerContainer.innerHTML = expiredText;
    }

    if (this.fixedText) {
      this.fixedText.innerHTML = expiredText;
    } else if (this.fixedContainer) {
      this.fixedContainer.innerHTML = expiredText;
    }
  }

  setupScrollListener() {
    if (!this.fixedElement) {
      return;
    }

    this.scrollHandler = this.handleScroll.bind(this);
    window.addEventListener('scroll', this.scrollHandler, { passive: true });
    this.handleScroll();
  }

  handleScroll() {
    if (!this.fixedElement) return;

    const scrollY = window.scrollY || window.pageYOffset;
    const shouldShowFixed = scrollY > 0;

    if (shouldShowFixed) {
      this.fixedElement.classList.add('is-active');
    } else {
      this.fixedElement.classList.remove('is-active');
    }
  }

  cleanup() {
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
      this.countdownInterval = null;
    }

    if (this.scrollHandler) {
      window.removeEventListener('scroll', this.scrollHandler);
      this.scrollHandler = null;
    }
  }

  getStorageValue(name) {
    if (typeof window === 'undefined' || !name) {
      return '';
    }

    try {
      return window.localStorage.getItem(name) || '';
    } catch (error) {
      return '';
    }
  }

  setStorageValue(name, value) {
    if (typeof window === 'undefined' || !name) {
      return;
    }

    try {
      window.localStorage.setItem(name, String(value));
    } catch (error) {
      // Ignore storage errors (private mode, quota, etc.)
    }
  }
}

customElements.define('header-countdown', HeaderCountdown);
